import { useMemo } from 'react';
import './hoursBox.css';
import {
  determinePriceByDate,
  minsInSpan,
  minsInSpanEdited,
  hoursInSpan,
} from '../utils/hoursUtils';

export const HoursBox = (props) => {

  const dateRangeLabel = useMemo(() => {
    if (!props.currentStartDate || !props.currentEndDate) return '';
    const trueEnd = new Date(props.currentEndDate);
    trueEnd.setDate(trueEnd.getDate() - 1);
    return `${props.currentStartDate} - ${trueEnd.getMonth()+1}/${trueEnd.getDate()}/${trueEnd.getFullYear()}`;
  }, [props.currentStartDate, props.currentEndDate]);

  const miscInSpan = (shifts) =>
    shifts.reduce((sum, s) => sum + (s.isFlatFee ? Number(s.flatFeeAmount) : 0), 0);

  const mileageInSpan = (shifts) =>
    shifts.reduce((sum, s) => sum + (s.shiftMileage != null && s.shiftMileage !== '' ? Number(s.shiftMileage) : 0), 0);

  const rows = useMemo(() => {
    const sorted = [...props.caretakerMasterList].sort((a, b) => a.name.localeCompare(b.name));
    const currentEndMs = new Date(props.currentEndDate).getTime();

    return sorted
      .filter(ct =>
        !ct.isDeactivated ||
        (ct.isDeactivated && currentEndMs <= ct.deactivationTime)
      )
      .map(ct => {
        const shiftsInSpan = props.eventList.filter(event => {
          const scheduledStart = (event.editedStartTime && event.originalStartTime)
            ? new Date(event.originalStartTime)
            : new Date(event.start);
          return (
            scheduledStart > new Date(props.currentStartDate) &&
            scheduledStart < new Date(props.currentEndDate) &&
            event.caretaker === ct.name
          );
        });

        const scheduledHours = Number(hoursInSpan(minsInSpan(shiftsInSpan)));
        const actualHours    = hoursInSpan(minsInSpanEdited(shiftsInSpan));
        const payRate        = determinePriceByDate(ct.pay, currentEndMs);
        const hourlyTotal    = (payRate * actualHours).toFixed(2);
        const misc           = miscInSpan(shiftsInSpan);
        const mileage        = mileageInSpan(shiftsInSpan);
        const total          = (payRate * actualHours + misc + mileage).toFixed(2);

        // Build per-client breakdown
        const byClientMap = {};
        for (const shift of shiftsInSpan) {
          const clientObj = props.clientList && props.clientList.find(c => c.name === shift.client);
          if (!clientObj || shift.client === 'Cleaning') continue;

          const packed = [shift];
          const hrs    = Number(hoursInSpan(minsInSpan(packed)));
          const hrsEd  = Number(hoursInSpan(minsInSpanEdited(packed)));
          const mi     = mileageInSpan(packed);
          const ms     = miscInSpan(packed);

          if (!byClientMap[shift.client]) {
            byClientMap[shift.client] = { hours: 0, hoursEdited: 0, mileage: 0, misc: 0 };
          }
          byClientMap[shift.client].hours       += hrs;
          byClientMap[shift.client].hoursEdited += hrsEd;
          byClientMap[shift.client].mileage     += mi;
          byClientMap[shift.client].misc        += ms;
        }

        const clientRows = Object.entries(byClientMap).map(([clientName, data]) => ({
          clientName,
          hours:        data.hours.toFixed(2),
          hoursEdited:  data.hoursEdited.toFixed(2),
          payRate,
          hourlyTotal:  (payRate * data.hoursEdited).toFixed(2),
          misc:         data.misc.toFixed(2),
          mileage:      data.mileage.toFixed(2),
          total:        (payRate * data.hoursEdited + data.misc + data.mileage).toFixed(2),
        }));

        return {
          name: ct.name,
          scheduledHours: scheduledHours.toFixed(2),
          actualHours,
          payRate,
          hourlyTotal,
          misc: misc.toFixed(2),
          mileage: mileage.toFixed(2),
          total,
          clientRows,
        };
      });
  }, [props.caretakerMasterList, props.eventList, props.clientList, props.currentStartDate, props.currentEndDate]);

  return (
    <div className="HoursBoxDiv" id="hoursTarget">
      <h2 id="hoursHeader">Caretaker Hours</h2>
      <h3 id="currentWeekHeader">{dateRangeLabel}</h3>
      <table id="caretakerTable">
        <thead>
          <tr>
            <td className="hoursBoxLabel">Name</td>
            <td className="hoursBoxLabel">Scheduled Hours</td>
            <td className="hoursBoxLabel">Actual Hours</td>
            <td className="hoursBoxLabel">Pay Rate</td>
            <td className="hoursBoxLabel">Hourly Total</td>
            <td className="hoursBoxLabel">Flat Rates</td>
            <td className="hoursBoxLabel">Mileage Pay</td>
            <td className="hoursBoxLabel">Total Wages</td>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <>
              <tr key={row.name} className="caretakerRowHours">
                <td className="caretakerHoursBoxTD">{row.name}</td>
                <td className="caretakerHoursBoxTD">{row.scheduledHours}</td>
                <td className="caretakerHoursBoxTD">{row.actualHours}</td>
                <td className="caretakerHoursBoxTD">${row.payRate}</td>
                <td className="caretakerHoursBoxTD">${row.hourlyTotal}</td>
                <td className="caretakerHoursBoxTD">${row.misc}</td>
                <td className="caretakerHoursBoxTD">${row.mileage}</td>
                <td className="caretakerHoursBoxTD">${row.total}</td>
              </tr>
              {row.clientRows.map(cr => (
                <tr key={`${row.name}-${cr.clientName}`} className="trByClient">
                  <td className="byClientTD">{cr.clientName}</td>
                  <td className="byClientTD">{cr.hours}</td>
                  <td className="byClientTD">{cr.hoursEdited}</td>
                  <td className="byClientTD">${cr.payRate}</td>
                  <td className="byClientTD">${cr.hourlyTotal}</td>
                  <td className="byClientTD">${cr.misc}</td>
                  <td className="byClientTD">${cr.mileage}</td>
                  <td className="byClientTD">${cr.total}</td>
                </tr>
              ))}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
};
